import React, { useState, useEffect, useContext, useRef, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { MapPin, CreditCard, ChevronDown, Ticket, ArrowLeft, CheckCircle, Building2, Shield, Zap, List, Map as MapIcon, Loader, Pill } from 'lucide-react';
import { toast } from 'react-toastify';
import { MemberContext } from '../App';
import axios from 'axios';
import CouponModal from '../components/CouponModal';
import { getPharmacyLogo, getPharmacyColor, getPharmacyShortName } from '../utils/pharmacyLogos';
import { TelehealthBanner } from '../components/TelehealthPromo';

const DrugPrices = () => {
  const { drugName } = useParams();
  const navigate = useNavigate();
  const { rxgoMemberId, setRxgoMemberId, member } = useContext(MemberContext);
  const [searchParams, setSearchParams] = useSearchParams();

  // Pharmacy filter state - read from URL
  const [pharmacyFilter, setPharmacyFilter] = useState(searchParams.get('pharmacy') || '');
  const [showPharmacyDropdown, setShowPharmacyDropdown] = useState(false);
  
  // Available pharmacies for filter
  const pharmacyOptions = [
    { value: '', label: 'All Pharmacies' },
    { value: 'CVS', label: 'CVS Pharmacy' },
    { value: 'Walgreens', label: 'Walgreens' },
    { value: 'Walmart', label: 'Walmart' },
    { value: 'Rite Aid', label: 'Rite Aid' },
    { value: 'Kroger', label: 'Kroger' },
    { value: 'Costco', label: 'Costco' },
    { value: 'Sam\'s Club', label: 'Sam\'s Club' },
    { value: 'Target', label: 'Target' },
    { value: 'Publix', label: 'Publix' },
    { value: 'H-E-B', label: 'H-E-B' },
    { value: 'Safeway', label: 'Safeway' },
    { value: 'Albertsons', label: 'Albertsons' },
    { value: 'Giant', label: 'Giant' },
    { value: 'Food Lion', label: 'Food Lion' },
    { value: 'Winn-Dixie', label: 'Winn-Dixie' },
    { value: 'Meijer', label: 'Meijer' },
    { value: 'Wegmans', label: 'Wegmans' },
    { value: 'Stop & Shop', label: 'Stop & Shop' },
    { value: 'Harris Teeter', label: 'Harris Teeter' },
    { value: 'Kinney', label: 'Kinney Drugs' },
    { value: 'Medicine Shoppe', label: 'Medicine Shoppe' },
    { value: 'Genoa', label: 'Genoa Healthcare' }
  ];

  const [loading, setLoading] = useState(true);
  const [drugData, setDrugData] = useState([]);
  const [drugGroups, setDrugGroups] = useState([]); // NEW: For Generic/Brand filter
  const [selectedDrugGroup, setSelectedDrugGroup] = useState(null); // NEW: Selected drug group (generic or brand)
  const [selectedDrug, setSelectedDrug] = useState(null);
  const [prices, setPrices] = useState([]);
  const [priceLoading, setPriceLoading] = useState(false);
  const [zipCode, setZipCode] = useState(localStorage.getItem('rxZipCode') || '');
  const [quantity, setQuantity] = useState(30);
  const [showDosageDropdown, setShowDosageDropdown] = useState(false);
  const [showDrugTypeDropdown, setShowDrugTypeDropdown] = useState(false); // NEW: Drug type dropdown
  const [detectingLocation, setDetectingLocation] = useState(false);
  
  // Track if initial prices have been loaded
  const initialPricesLoaded = useRef(false);
  const zipDetectedRef = useRef(false);
  
  // Mobile detection
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    const handleClickOutside = (e) => {
      if (!e.target.closest('.pharmacy-filter-dropdown')) {
        setShowPharmacyDropdown(false);
      }
    };
    window.addEventListener('resize', handleResize);
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      window.removeEventListener('resize', handleResize);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  
  // View mode: 'list' or 'map' - DEFAULT TO LIST ON MOBILE
  const [viewMode, setViewMode] = useState(window.innerWidth < 768 ? 'list' : 'map');
  
  // Map refs
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markersRef = useRef([]);
  const infoWindowRef = useRef(null);
  const [mapKey, setMapKey] = useState(0); // Key to force map recreation
  
  // Coupon Modal State
  const [showCouponModal, setShowCouponModal] = useState(false);
  const [selectedCoupon, setSelectedCoupon] = useState(null);

  const colors = {
    primary: '#2055df',
    primaryDark: '#1a44b8',
    primaryLight: '#4d7df7',
    secondary: '#0f172a',
    accent: '#06b6d4',
    success: '#10b981',
    bg: '#f8fafc',
    white: '#ffffff',
    gray100: '#f1f5f9',
    gray200: '#e2e8f0',
    gray300: '#cbd5e1',
    gray400: '#94a3b8',
    gray500: '#64748b',
    gray600: '#475569',
    gray700: '#334155',
    gray800: '#1e293b',
    gray900: '#0f172a'
  };

  // Filter prices based on pharmacy filter - improved matching
  const filteredPrices = pharmacyFilter 
    ? prices.filter(p => {
        const name = p.pharmacyName.toLowerCase();
        const filter = pharmacyFilter.toLowerCase();
        
        // Direct match - pharmacy name contains the filter
        if (name.includes(filter)) return true;
        
        // "Target" filter should include "CVS Inside Target" 
        if (filter === 'target' && name.includes('target')) {
          return true;
        }
        
        // Handle corporate family groupings (same parent company)
        const familyGroups = {
          'kroger': ['kroger', 'fred meyer', 'fry\'s', 'ralphs', 'smith\'s', 'king soopers', 'qfc', 'dillons', 'city market'],
          'albertsons': ['albertsons', 'safeway', 'vons', 'jewel-osco', 'acme', 'shaw\'s', 'star market', 'randalls', 'tom thumb'],
          'walmart': ['walmart'],
          'sam\'s club': ['sam\'s club', 'sams club'],
          'rite aid': ['rite aid', 'rite-aid', 'riteaid'],
          'walgreens': ['walgreens', 'duane reade']
        };
        
        // Check if filter matches a family group
        if (familyGroups[filter]) {
          return familyGroups[filter].some(member => name.includes(member));
        }
        
        return false;
      })
    : prices;

  // Load Google Maps script
  useEffect(() => {
    if (window.google?.maps) return;
    
    const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || process.env.REACT_APP_GOOGLE_MAPS_KEY;
    if (!apiKey) {
      console.warn('Google Maps API key not found');
      return;
    }

    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`;
    script.async = true;
    script.defer = true;
    document.head.appendChild(script);
  }, []);

  // Auto-detect ZIP code - IMPROVED to trigger price fetch
  useEffect(() => {
    const detectZipCode = async () => {
      if (zipCode && zipCode.length === 5) return;
      
      setDetectingLocation(true);
      
      try {
        if (navigator.geolocation) {
          navigator.geolocation.getCurrentPosition(
            async (position) => {
              try {
                const { latitude, longitude } = position.coords;
                const response = await axios.get(
                  `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&zoom=18&addressdetails=1`,
                  { headers: { 'User-Agent': 'RxChatWithDr/1.0' } }
                );
                
                const detectedZip = response.data?.address?.postcode;
                if (detectedZip && /^\d{5}/.test(detectedZip)) {
                  const zip5 = detectedZip.substring(0, 5);
                  setZipCode(zip5);
                  localStorage.setItem('rxZipCode', zip5);
                  zipDetectedRef.current = true; // Mark that we detected ZIP
                  toast.success(`Location detected: ${zip5}`);
                }
              } catch (err) {
                await detectByIP();
              }
              setDetectingLocation(false);
            },
            async () => {
              await detectByIP();
              setDetectingLocation(false);
            },
            { timeout: 5000, maximumAge: 300000 }
          );
        } else {
          await detectByIP();
          setDetectingLocation(false);
        }
      } catch (err) {
        setDetectingLocation(false);
      }
    };

    const detectByIP = async () => {
      try {
        const response = await axios.get('/api/geolocation');
        const detectedZip = response.data?.postal;
        if (detectedZip && /^\d{5}/.test(detectedZip)) {
          const zip5 = detectedZip.substring(0, 5);
          setZipCode(zip5);
          localStorage.setItem('rxZipCode', zip5);
          zipDetectedRef.current = true; // Mark that we detected ZIP
          if (response.data?.city) {
            toast.success(`Location detected: ${response.data.city} (${zip5})`);
          } else {
            toast.success(`Location detected: ${zip5}`);
          }
        }
      } catch (err) {
        console.warn('Geolocation failed:', err.message);
        const defaultZip = '10001';
        setZipCode(defaultZip);
        localStorage.setItem('rxZipCode', defaultZip);
        zipDetectedRef.current = true;
      }
    };

    detectZipCode();
  }, []);

  useEffect(() => {
    if (drugName) fetchDrugData();
  }, [drugName]);

  // Auto-fetch prices when drug is selected and ZIP is available
  useEffect(() => {
    if (selectedDrug && zipCode.length === 5) {
      fetchPrices();
    }
  }, [selectedDrug, quantity]);

  // NEW: Auto-fetch prices when ZIP is detected AFTER drug data is loaded
  useEffect(() => {
    if (selectedDrug && zipCode.length === 5 && !initialPricesLoaded.current) {
      initialPricesLoaded.current = true;
      fetchPrices();
    }
  }, [zipCode, selectedDrug]);

  // IMPROVED: Fetch drug data and use drugGroups from API for Generic/Brand filter
  const fetchDrugData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`/api/drugs/dosages/${encodeURIComponent(drugName)}`, {
        headers: { 'rxgo-member-id': rxgoMemberId }
      });
      
      if (response.data.memberId) setRxgoMemberId(response.data.memberId);
      
      const dosages = response.data.dosages || [];
      const groups = response.data.drugGroups || []; // NEW: Use drugGroups from API
      
      setDrugData(dosages);
      
      // Use drugGroups from API if available, otherwise group manually
      if (groups.length > 0) {
        setDrugGroups(groups);
        
        // Select the generic group first (or first available)
        const firstGroup = groups.find(g => g.generic && g.selected) || groups.find(g => g.generic) || groups[0];
        setSelectedDrugGroup(firstGroup);
        
        if (firstGroup.dosages && firstGroup.dosages.length > 0) {
          setSelectedDrug(firstGroup.dosages[0]);
          setQuantity(firstGroup.dosages[0].defaultQuantity || 30);
        }
      } else if (dosages.length > 0) {
        // Fallback: Group dosages manually by drugName
        const groupMap = {};
        dosages.forEach(d => {
          const key = d.drugName || drugName;
          if (!groupMap[key]) {
            groupMap[key] = {
              drugName: d.drugName || drugName,
              generic: d.generic !== false,
              dosages: []
            };
          }
          groupMap[key].dosages.push(d);
        });
        
        const groupArray = Object.values(groupMap);
        setDrugGroups(groupArray);
        
        const firstGroup = groupArray.find(g => g.generic) || groupArray[0];
        setSelectedDrugGroup(firstGroup);
        
        if (firstGroup.dosages.length > 0) {
          setSelectedDrug(firstGroup.dosages[0]);
          setQuantity(firstGroup.dosages[0].defaultQuantity || 30);
        }
      }
    } catch (error) {
      console.error('Error fetching drug data:', error);
      toast.error('Failed to load medication information');
    } finally {
      setLoading(false);
    }
  };

  const fetchPrices = async () => {
    if (!selectedDrug) return;
    
    if (zipCode.length !== 5) {
      toast.error('Please enter a valid 5-digit ZIP code');
      return;
    }
    
    setPriceLoading(true);
    localStorage.setItem('rxZipCode', zipCode);
    
    // Increment mapKey to force fresh map recreation
    setMapKey(prev => prev + 1);
    
    try {
      const response = await axios.get(`/api/drugs/prices/${encodeURIComponent(drugName)}`, {
        params: { ndc: selectedDrug.ndc, quantity, zip: zipCode },
        headers: { 'rxgo-member-id': rxgoMemberId }
      });
      
      if (response.data.memberId) setRxgoMemberId(response.data.memberId);
      setPrices(response.data.prices || []);
    } catch (error) {
      console.error('Error fetching prices:', error);
      setPrices([]);
    } finally {
      setPriceLoading(false);
    }
  };

  const initializeMap = useCallback(() => {
    if (!window.google?.maps || !mapRef.current || !zipCode) return;

    const google = window.google;
    const geocoder = new google.maps.Geocoder();

    // Clear existing markers
    markersRef.current.forEach(marker => marker.setMap(null));
    markersRef.current = [];

    // Close any open info window
    if (infoWindowRef.current) {
      infoWindowRef.current.close();
    }

    // Properly destroy old map instance
    if (mapInstanceRef.current) {
      google.maps.event.clearInstanceListeners(mapInstanceRef.current);
      mapInstanceRef.current = null;
    }

    geocoder.geocode({ address: zipCode + ', USA' }, (results, status) => {
      if (status !== 'OK' || !results[0]) return;

      const center = results[0].geometry.location;

      // Create fresh map
      mapInstanceRef.current = new google.maps.Map(mapRef.current, {
        center: center,
        zoom: 11,
        styles: [
          { featureType: 'poi.business', stylers: [{ visibility: 'off' }] },
          { featureType: 'poi.park', elementType: 'labels.text', stylers: [{ visibility: 'off' }] }
        ],
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: true,
        rotateControl: false,
        tilt: 0,
        heading: 0,
        gestureHandling: 'cooperative',
        mapTypeId: 'roadmap',
        disableDefaultUI: false,
        keyboardShortcuts: false
      });
      
      infoWindowRef.current = new google.maps.InfoWindow();

      const map = mapInstanceRef.current;
      const placesService = new google.maps.places.PlacesService(map);
      const bounds = new google.maps.LatLngBounds();
      bounds.extend(center);

      filteredPrices.forEach((pharmacy, index) => {
        const searchQuery = `${pharmacy.pharmacyName} pharmacy near ${zipCode}`;
        
        placesService.findPlaceFromQuery({
          query: searchQuery,
          fields: ['name', 'geometry', 'formatted_address', 'place_id']
        }, (results, status) => {
          if (status === google.maps.places.PlacesServiceStatus.OK && results?.[0]) {
            const place = results[0];
            const position = place.geometry.location;

            const marker = new google.maps.Marker({
              position: position,
              map: map,
              title: pharmacy.pharmacyName,
              icon: {
                path: google.maps.SymbolPath.CIRCLE,
                scale: 12,
                fillColor: index === 0 ? colors.success : colors.primary,
                fillOpacity: 1,
                strokeColor: '#ffffff',
                strokeWeight: 3
              },
              label: {
                text: '$' + Math.round(pharmacy.price),
                color: '#ffffff',
                fontSize: '10px',
                fontWeight: 'bold'
              },
              zIndex: index === 0 ? 1000 : 100 - index
            });

            pharmacy.address = place.formatted_address;
            pharmacy.lat = position.lat();
            pharmacy.lng = position.lng();
            
            const safePharmacyName = pharmacy.pharmacyName.replace(/'/g, "\\'").replace(/"/g, "&quot;");

            const infoContent = `
              <div style="padding: 12px; min-width: 200px; font-family: -apple-system, sans-serif;">
                <h3 style="margin: 0 0 8px; font-size: 14px; font-weight: 600; color: #1e293b;">
                  ${pharmacy.pharmacyName}
                  ${index === 0 ? '<span style="background: #10b981; color: white; padding: 2px 6px; border-radius: 4px; font-size: 9px; margin-left: 6px;">LOWEST</span>' : ''}
                </h3>
                <p style="margin: 0 0 8px; font-size: 11px; color: #64748b; line-height: 1.3;">
                  ${place.formatted_address || ''}
                </p>
                <p style="margin: 0 0 4px; font-size: 22px; font-weight: 700; color: #10b981;">
                  $${pharmacy.price.toFixed(2)}
                </p>
                <div style="display: flex; gap: 6px; margin-top: 10px;">
                  <button 
                    onclick="window.dispatchEvent(new CustomEvent('openCoupon', { detail: { pharmacy: '${safePharmacyName}', price: ${pharmacy.price}, index: ${index} } }))"
                    style="flex: 1; padding: 8px; background: #10b981; color: white; border: none; border-radius: 6px; font-size: 11px; font-weight: 600; cursor: pointer;"
                  >
                    Get Coupon
                  </button>
                  <button 
                    onclick="window.open('https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(place.formatted_address || pharmacy.pharmacyName)}', '_blank')"
                    style="flex: 1; padding: 8px; background: #2055df; color: white; border: none; border-radius: 6px; font-size: 11px; font-weight: 600; cursor: pointer;"
                  >
                    Directions
                  </button>
                </div>
              </div>
            `;

            marker.addListener('click', () => {
              infoWindowRef.current.setContent(infoContent);
              infoWindowRef.current.open(map, marker);
            });

            markersRef.current.push(marker);
            bounds.extend(position);

            // After all markers are added, fit bounds
            if (markersRef.current.length === filteredPrices.length || index === filteredPrices.length - 1) {
              setTimeout(() => {
                try {
                  // Only fit bounds if we have markers
                  if (!bounds.isEmpty()) {
                    map.fitBounds(bounds, { top: 50, right: 50, bottom: 50, left: 50 });
                    
                    // Use bounds_changed event to adjust zoom after fitBounds completes
                    google.maps.event.addListenerOnce(map, 'bounds_changed', () => {
                      const currentZoom = map.getZoom();
                      // Constrain zoom level
                      if (currentZoom > 15) map.setZoom(15);
                      if (currentZoom < 10) map.setZoom(10);
                    });
                  }
                } catch (e) {
                  console.warn('Error fitting bounds:', e);
                }
              }, 100);
            }
          }
        });
      });
    });
  }, [filteredPrices, zipCode, colors]);

  // Initialize map when conditions are met
  useEffect(() => {
    if (viewMode === 'map' && filteredPrices.length > 0 && zipCode) {
      // Small delay to ensure DOM is updated after mapKey change
      const timer = setTimeout(() => {
        initializeMap();
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [viewMode, filteredPrices, zipCode, isMobile, pharmacyFilter, mapKey, initializeMap]);

  useEffect(() => {
    const handleOpenCoupon = (e) => {
      const { pharmacy, price } = e.detail;
      // Use the selected drug group's name (could be brand name like "Norvasc")
      const displayDrugName = selectedDrugGroup?.drugName || drugName;
      
      setSelectedCoupon({
        pharmacy,
        price,
        ndc: selectedDrug?.ndc,
        quantity,
        zip: zipCode,
        drugName: displayDrugName,
        dosage: formatDosage(selectedDrug)
      });
      setShowCouponModal(true);
    };

    window.addEventListener('openCoupon', handleOpenCoupon);
    return () => window.removeEventListener('openCoupon', handleOpenCoupon);
  }, [selectedDrug, selectedDrugGroup, quantity, zipCode, drugName]);

  const handleGetCoupon = (price) => {
    // Use the selected drug group's name (could be brand name like "Norvasc")
    const displayDrugName = selectedDrugGroup?.drugName || drugName;
    
    setSelectedCoupon({
      pharmacy: price.pharmacyName,
      price: price.price,  // Pass the price
      ndc: selectedDrug?.ndc,
      quantity,
      zip: zipCode,
      drugName: displayDrugName,  // Use selected group's drug name
      couponUrl: price.couponUrl,
      dosage: formatDosage(selectedDrug)  // Include dosage info
    });
    setShowCouponModal(true);
  };

  // NEW: Handle drug group change (Generic vs Brand)
  const handleDrugGroupChange = (group) => {
    setSelectedDrugGroup(group);
    setShowDrugTypeDropdown(false);
    
    // Select the first dosage of the new group
    if (group.dosages.length > 0) {
      setSelectedDrug(group.dosages[0]);
      setQuantity(group.dosages[0].defaultQuantity || 30);
    }
  };

  const formatDosage = (drug) => {
    if (!drug) return 'Select Dosage';
    if (drug.dosage) return drug.dosage.toUpperCase();
    if (drug.strength && drug.form) return `${drug.strength} ${drug.form}`.toUpperCase();
    if (drug.dosageForm && drug.strength) return `${drug.strength} ${drug.dosageForm}`.toUpperCase();
    return drug.dosageForm?.toUpperCase() || drug.form?.toUpperCase() || 'TABLET';
  };

  const formatName = (name) => {
    if (!name) return '';
    return name.toLowerCase().split(' ').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  };

  // FIXED: Pharmacy Logo component - uses backend proxy to bypass CORS
  const PharmacyLogoImage = ({ name, logoUrl, size = 40 }) => {
    const [imgError, setImgError] = useState(false);
    const [clearbitError, setClearbitError] = useState(false);
    const brandColor = getPharmacyColor(name);
    const shortName = getPharmacyShortName(name);
    
    // Get Clearbit logo URL from our utility
    const clearbitLogo = getPharmacyLogo(name);
    
    // Use proxy endpoint to bypass CORS for API-provided logos
    const proxiedUrl = logoUrl ? `/api/pharmacy-logo?url=${encodeURIComponent(logoUrl)}` : null;
    
    // Try API-provided logo first
    if (proxiedUrl && !imgError) {
      return (
        <img
          src={proxiedUrl}
          alt={name}
          onError={() => setImgError(true)}
          style={{
            width: size,
            height: size,
            borderRadius: 10,
            objectFit: 'contain',
            background: 'white',
            padding: 4,
            border: `1px solid ${colors.gray200}`,
            flexShrink: 0
          }}
        />
      );
    }
    
    // Try Clearbit logo as second option
    if (clearbitLogo && !clearbitError) {
      return (
        <img
          src={clearbitLogo}
          alt={name}
          onError={() => setClearbitError(true)}
          style={{
            width: size,
            height: size,
            borderRadius: 10,
            objectFit: 'contain',
            background: 'white',
            padding: 4,
            border: `1px solid ${colors.gray200}`,
            flexShrink: 0
          }}
        />
      );
    }
    
    // Use colored box with initials as final fallback
    return (
      <div style={{
        width: size,
        height: size,
        borderRadius: 10,
        background: brandColor,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: size * 0.35,
        fontWeight: 800,
        color: 'white',
        flexShrink: 0,
        letterSpacing: '-0.5px'
      }}>
        {shortName}
      </div>
    );
  };

  const focusPharmacy = (index) => {
    if (mapInstanceRef.current && markersRef.current[index]) {
      const marker = markersRef.current[index];
      mapInstanceRef.current.panTo(marker.getPosition());
      mapInstanceRef.current.setZoom(15);
      window.google.maps.event.trigger(marker, 'click');
    }
  };

  // Get dosages for current selected drug group
  const currentDosages = selectedDrugGroup?.dosages || drugData;

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <div style={{
          width: 48, height: 48, border: `3px solid ${colors.gray200}`,
          borderTopColor: colors.primary, borderRadius: '50%',
          animation: 'spin 0.8s linear infinite'
        }} />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  return (
    <div style={{ fontFamily: "'Inter', -apple-system, sans-serif", background: colors.bg, minHeight: '100vh' }}>
      {/* Header - Extra top padding for fixed header */}
      <div style={{
        background: colors.primary,
        padding: isMobile ? '100px 16px 32px' : '120px 24px 48px',
        color: 'white',
        marginTop: 0
      }}>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          <button
            onClick={() => navigate(-1)}
            style={{
              display: 'flex', alignItems: 'center', gap: 6,
              background: 'rgba(255,255,255,0.1)', border: 'none',
              padding: '8px 14px', borderRadius: 8, color: 'white',
              fontSize: 14, cursor: 'pointer', marginBottom: isMobile ? 16 : 24
            }}
          >
            <ArrowLeft size={16} /> Back
          </button>
          
          <h1 style={{ fontSize: isMobile ? 24 : 32, fontWeight: 700, marginBottom: 8, color: 'white' }}>
            {formatName(drugName)} Prices
          </h1>
          <p style={{ fontSize: isMobile ? 14 : 16, opacity: 0.9, color: 'white' }}>
            Compare prices and save up to 80% with our free discount card
          </p>
        </div>
      </div>

      {/* Filters - COMPACT LAYOUT with NEW Generic/Brand filter */}
      <div style={{ background: 'white', borderBottom: `1px solid ${colors.gray200}`, position: 'sticky', top: 0, zIndex: 50 }}>
        <div style={{ maxWidth: 900, margin: '0 auto', padding: isMobile ? '12px 16px' : '16px 24px' }}>
          <div style={{ 
            display: 'flex', 
            flexDirection: isMobile ? 'column' : 'row',
            alignItems: isMobile ? 'stretch' : 'center',
            gap: 10
          }}>
            {/* NEW: Generic/Brand Dropdown - Shows full drug name */}
            {drugGroups.length > 1 && (
              <div style={{ position: 'relative', flex: isMobile ? 1 : '0 0 200px' }}>
                <button
                  onClick={() => setShowDrugTypeDropdown(!showDrugTypeDropdown)}
                  style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    width: '100%', padding: '10px 12px', background: 'white',
                    border: `2px solid ${colors.primary}20`, borderRadius: 10,
                    fontSize: 14, cursor: 'pointer', color: colors.gray800,
                    background: `${colors.primary}05`
                  }}
                >
                  <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <Pill size={14} style={{ color: colors.primary }} />
                    <span style={{ fontWeight: 500 }}>
                      {formatName(selectedDrugGroup?.drugName)} ({selectedDrugGroup?.generic ? 'Generic' : 'Brand'})
                    </span>
                  </span>
                  <ChevronDown size={16} style={{ color: colors.gray400 }} />
                </button>
                
                {showDrugTypeDropdown && (
                  <div style={{
                    position: 'absolute', top: '100%', left: 0, right: 0,
                    background: 'white', borderRadius: 10, marginTop: 4,
                    border: `1px solid ${colors.gray200}`,
                    boxShadow: '0 10px 40px rgba(0,0,0,0.15)',
                    overflow: 'hidden', zIndex: 100
                  }}>
                    {drugGroups.map((group, i) => (
                      <button
                        key={i}
                        onClick={() => handleDrugGroupChange(group)}
                        style={{
                          width: '100%', padding: '12px 16px', border: 'none',
                          background: selectedDrugGroup?.drugName === group.drugName 
                            ? (group.generic ? '#e8f5e9' : '#fff3e0')
                            : 'white',
                          textAlign: 'left', cursor: 'pointer', fontSize: 14,
                          borderBottom: i < drugGroups.length - 1 ? `1px solid ${colors.gray100}` : 'none',
                          display: 'flex', alignItems: 'center', gap: 8
                        }}
                      >
                        <span style={{
                          padding: '2px 8px',
                          borderRadius: 4,
                          fontSize: 11,
                          fontWeight: 600,
                          background: group.generic ? '#e8f5e9' : '#fff3e0',
                          color: group.generic ? '#2e7d32' : '#e65100'
                        }}>
                          {group.generic ? 'Generic' : 'Brand'}
                        </span>
                        <span style={{ fontWeight: 500, color: colors.gray800 }}>
                          {formatName(group.drugName)}
                        </span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Dosage Dropdown */}
            <div style={{ position: 'relative', flex: isMobile ? 1 : '0 0 200px' }}>
              <button
                onClick={() => setShowDosageDropdown(!showDosageDropdown)}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  width: '100%', padding: '10px 12px', background: 'white',
                  border: `2px solid ${colors.gray200}`, borderRadius: 10,
                  fontSize: 14, cursor: 'pointer', color: colors.gray800
                }}
              >
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, textAlign: 'left' }}>
                  {formatDosage(selectedDrug)}
                </span>
                <ChevronDown size={16} style={{ color: colors.gray400, flexShrink: 0 }} />
              </button>
              
              {showDosageDropdown && (
                <div style={{
                  position: 'absolute', top: '100%', left: 0, right: 0,
                  background: 'white', borderRadius: 10, marginTop: 4,
                  border: `1px solid ${colors.gray200}`,
                  boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
                  maxHeight: 300, overflow: 'auto', zIndex: 100
                }}>
                  {currentDosages.map((drug, i) => (
                    <button
                      key={drug.ndc || i}
                      onClick={() => { setSelectedDrug(drug); setShowDosageDropdown(false); }}
                      style={{
                        width: '100%', padding: '12px 16px', border: 'none',
                        background: selectedDrug?.ndc === drug.ndc ? colors.gray100 : 'white',
                        textAlign: 'left', cursor: 'pointer', fontSize: 14,
                        borderBottom: `1px solid ${colors.gray100}`,
                        color: colors.gray800
                      }}
                    >
                      {formatDosage(drug)}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Quantity */}
            <select
              value={quantity}
              onChange={(e) => setQuantity(Number(e.target.value))}
              style={{
                padding: '10px 12px', 
                border: `2px solid ${colors.gray200}`,
                borderRadius: 10, fontSize: 14, background: 'white',
                color: colors.gray800, cursor: 'pointer', 
                width: isMobile ? '100%' : 110,
                flexShrink: 0
              }}
            >
              {[7, 14, 15, 21, 28, 30, 60, 90, 100, 120, 180].map(q => (
                <option key={q} value={q}>{q} pills</option>
              ))}
            </select>

            {/* ZIP Code */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'white', border: `2px solid ${colors.gray200}`, borderRadius: 10, padding: '0 10px', width: isMobile ? '100%' : 120, flexShrink: 0 }}>
              {detectingLocation ? (
                <Loader size={16} style={{ color: colors.primary, animation: 'spin 1s linear infinite', flexShrink: 0 }} />
              ) : (
                <MapPin size={16} style={{ color: colors.gray400, flexShrink: 0 }} />
              )}
              <input
                type="text"
                value={zipCode}
                onChange={(e) => {
                  const newZip = e.target.value.replace(/\D/g, '').slice(0, 5);
                  setZipCode(newZip);
                  if (newZip.length === 5) localStorage.setItem('rxZipCode', newZip);
                }}
                placeholder="ZIP"
                maxLength={5}
                style={{
                  padding: '10px 0', 
                  border: 'none',
                  outline: 'none',
                  fontSize: 14, 
                  width: '100%',
                  color: colors.gray800
                }}
              />
            </div>

            {/* Pharmacy Filter */}
            <div className="pharmacy-filter-dropdown" style={{ position: 'relative' }}>
              <button
                onClick={() => setShowPharmacyDropdown(!showPharmacyDropdown)}
                style={{
                  padding: '10px 12px',
                  border: `2px solid ${pharmacyFilter ? colors.primary : colors.gray200}`,
                  borderRadius: 10,
                  fontSize: 14,
                  background: pharmacyFilter ? `${colors.primary}10` : 'white',
                  color: colors.gray800,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  width: isMobile ? '100%' : 160,
                  flexShrink: 0
                }}
              >
                <Building2 size={16} style={{ color: pharmacyFilter ? colors.primary : colors.gray400, flexShrink: 0 }} />
                <span style={{ flex: 1, textAlign: 'left', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {pharmacyFilter || 'All Pharmacies'}
                </span>
                <ChevronDown size={16} style={{ color: colors.gray400, flexShrink: 0 }} />
              </button>
              
              {showPharmacyDropdown && (
                <div style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 0,
                  background: 'white',
                  borderRadius: 10,
                  boxShadow: '0 10px 40px rgba(0,0,0,0.15)',
                  marginTop: 4,
                  maxHeight: 250,
                  overflow: 'auto',
                  zIndex: 100,
                  minWidth: 180
                }}>
                  {pharmacyOptions.map((p, i) => (
                    <button
                      key={i}
                      onClick={() => {
                        setPharmacyFilter(p.value);
                        setShowPharmacyDropdown(false);
                        // Update URL
                        if (p.value) {
                          searchParams.set('pharmacy', p.value);
                        } else {
                          searchParams.delete('pharmacy');
                        }
                        setSearchParams(searchParams);
                      }}
                      style={{
                        width: '100%',
                        padding: '10px 14px',
                        border: 'none',
                        background: pharmacyFilter === p.value ? colors.gray100 : 'white',
                        textAlign: 'left',
                        cursor: 'pointer',
                        fontSize: 14,
                        color: colors.gray800,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8
                      }}
                    >
                      {p.value && (
                        <div style={{
                          width: 24,
                          height: 24,
                          borderRadius: 6,
                          background: getPharmacyColor(p.label),
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: 10,
                          fontWeight: 700,
                          color: 'white'
                        }}>
                          {getPharmacyShortName(p.label)}
                        </div>
                      )}
                      {p.label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Update Button */}
            <button
              onClick={fetchPrices}
              disabled={priceLoading}
              style={{
                padding: '10px 18px', 
                background: colors.primary,
                color: 'white', border: 'none', borderRadius: 10,
                fontSize: 14, fontWeight: 600, cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                opacity: priceLoading ? 0.7 : 1,
                whiteSpace: 'nowrap',
                flexShrink: 0,
                width: isMobile ? '100%' : 'auto'
              }}
            >
              <Zap size={16} /> Update
            </button>

            {/* View Toggle - Desktop only inline */}
            {!isMobile && (
              <div style={{ 
                display: 'flex',
                background: colors.gray100, 
                borderRadius: 10, 
                padding: 3,
                marginLeft: 'auto'
              }}>
                <button
                  onClick={() => setViewMode('list')}
                  style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4,
                    padding: '8px 12px', border: 'none', borderRadius: 8,
                    background: viewMode === 'list' ? 'white' : 'transparent',
                    color: viewMode === 'list' ? colors.primary : colors.gray500,
                    fontSize: 13, fontWeight: 500, cursor: 'pointer',
                    boxShadow: viewMode === 'list' ? '0 2px 8px rgba(0,0,0,0.1)' : 'none'
                  }}
                >
                  <List size={14} /> List
                </button>
                <button
                  onClick={() => setViewMode('map')}
                  style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4,
                    padding: '8px 12px', border: 'none', borderRadius: 8,
                    background: viewMode === 'map' ? 'white' : 'transparent',
                    color: viewMode === 'map' ? colors.primary : colors.gray500,
                    fontSize: 13, fontWeight: 500, cursor: 'pointer',
                    boxShadow: viewMode === 'map' ? '0 2px 8px rgba(0,0,0,0.1)' : 'none'
                  }}
                >
                  <MapIcon size={14} /> Map
                </button>
              </div>
            )}
          </div>

          {/* View Toggle - Mobile only (separate row) */}
          {isMobile && (
            <div style={{ 
              display: 'flex',
              justifyContent: 'center',
              background: colors.gray100, 
              borderRadius: 10, 
              padding: 4,
              marginTop: 10
            }}>
              <button
                onClick={() => setViewMode('list')}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                  padding: '10px 16px', border: 'none', borderRadius: 8,
                  background: viewMode === 'list' ? 'white' : 'transparent',
                  color: viewMode === 'list' ? colors.primary : colors.gray500,
                  fontSize: 14, fontWeight: 500, cursor: 'pointer',
                  boxShadow: viewMode === 'list' ? '0 2px 8px rgba(0,0,0,0.1)' : 'none',
                  flex: 1
                }}
              >
                <List size={16} /> List
              </button>
              <button
                onClick={() => setViewMode('map')}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                  padding: '10px 16px', border: 'none', borderRadius: 8,
                  background: viewMode === 'map' ? 'white' : 'transparent',
                  color: viewMode === 'map' ? colors.primary : colors.gray500,
                  fontSize: 14, fontWeight: 500, cursor: 'pointer',
                  boxShadow: viewMode === 'map' ? '0 2px 8px rgba(0,0,0,0.1)' : 'none',
                  flex: 1
                }}
              >
                <MapIcon size={16} /> Map
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div style={{ maxWidth: 1100, margin: '0 auto', padding: isMobile ? '16px' : '32px 24px' }}>
        {/* Benefits Bar */}
        <div style={{
          display: 'grid', 
          gridTemplateColumns: 'repeat(2, 1fr)',
          gap: 12, 
          marginBottom: isMobile ? 20 : 32, 
          padding: isMobile ? 16 : 24, 
          background: 'white',
          borderRadius: 12, 
          border: `1px solid ${colors.gray200}`
        }}>
          {[
            { icon: <CheckCircle size={isMobile ? 16 : 20} />, text: '100% Free' },
            { icon: <Shield size={isMobile ? 16 : 20} />, text: 'No Sign-up' },
            { icon: <Building2 size={isMobile ? 16 : 20} />, text: '67K+ Pharmacies' },
            { icon: <Zap size={isMobile ? 16 : 20} />, text: 'Instant Savings' }
          ].map((item, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{ color: colors.success, flexShrink: 0 }}>{item.icon}</div>
              <span style={{ fontSize: isMobile ? 13 : 14, fontWeight: 500, color: colors.gray700 }}>{item.text}</span>
            </div>
          ))}
        </div>

        {/* Prices Header */}
        <div style={{ marginBottom: isMobile ? 16 : 24 }}>
          <h2 style={{ fontSize: isMobile ? 20 : 24, fontWeight: 700, color: colors.gray900, marginBottom: 6 }}>
            Pharmacy Prices for {formatName(drugName)}
          </h2>
          <p style={{ fontSize: isMobile ? 14 : 15, color: colors.gray600 }}>
            {selectedDrug ? `${quantity} × ${formatDosage(selectedDrug)}` : 'Select a dosage to see prices'}
          </p>
        </div>

        {/* Loading State */}
        {priceLoading ? (
          <div style={{ textAlign: 'center', padding: 40 }}>
            <div style={{
              width: 40, height: 40, border: `3px solid ${colors.gray200}`,
              borderTopColor: colors.primary, borderRadius: '50%',
              animation: 'spin 0.8s linear infinite', margin: '0 auto'
            }} />
            <p style={{ marginTop: 16, color: colors.gray500, fontSize: 14 }}>Loading prices...</p>
          </div>
        ) : filteredPrices.length === 0 && prices.length > 0 ? (
          /* No results for filter but have prices */
          <div style={{
            textAlign: 'center', padding: 40, background: 'white',
            borderRadius: 16, border: `1px solid ${colors.gray200}`
          }}>
            <Building2 size={40} style={{ color: colors.gray300, marginBottom: 12 }} />
            <h3 style={{ color: colors.gray700, marginBottom: 8, fontSize: 18 }}>No {pharmacyFilter} Found Nearby</h3>
            <p style={{ color: colors.gray500, fontSize: 14, marginBottom: 16 }}>
              Try selecting "All Pharmacies" or a different pharmacy
            </p>
            <button
              onClick={() => {
                setPharmacyFilter('');
                searchParams.delete('pharmacy');
                setSearchParams(searchParams);
              }}
              style={{
                padding: '10px 20px',
                background: colors.primary,
                color: 'white',
                border: 'none',
                borderRadius: 8,
                fontSize: 14,
                fontWeight: 600,
                cursor: 'pointer'
              }}
            >
              Show All {prices.length} Pharmacies
            </button>
          </div>
        ) : filteredPrices.length === 0 ? (
          <div style={{
            textAlign: 'center', padding: 40, background: 'white',
            borderRadius: 16, border: `1px solid ${colors.gray200}`
          }}>
            <Building2 size={40} style={{ color: colors.gray300, marginBottom: 12 }} />
            <h3 style={{ color: colors.gray700, marginBottom: 8, fontSize: 18 }}>No Prices Found</h3>
            <p style={{ color: colors.gray500, fontSize: 14 }}>
              {zipCode ? 'Try a different ZIP code or dosage' : 'Enter your ZIP code to see prices'}
            </p>
          </div>
        ) : viewMode === 'map' ? (
          /* MAP VIEW - RESPONSIVE */
          <div style={{ 
            display: isMobile ? 'flex' : 'grid', 
            flexDirection: isMobile ? 'column' : undefined,
            gridTemplateColumns: isMobile ? undefined : '340px 1fr', 
            gap: isMobile ? 16 : 24, 
            height: isMobile ? 'auto' : 600 
          }}>
            {/* Map Container - Shows first on mobile */}
            <div
              key={`map-container-${mapKey}`}
              ref={mapRef}
              style={{
                borderRadius: 16, overflow: 'hidden', 
                height: isMobile ? 300 : '100%',
                background: colors.gray100, border: `1px solid ${colors.gray200}`,
                order: isMobile ? -1 : 1
              }}
            />

            {/* Sidebar/List */}
            <div style={{ 
              background: 'white', borderRadius: 16, overflow: 'hidden',
              border: `1px solid ${colors.gray200}`, display: 'flex', flexDirection: 'column',
              maxHeight: isMobile ? 400 : 'none'
            }}>
              <div style={{ padding: isMobile ? 14 : 16, borderBottom: `1px solid ${colors.gray200}` }}>
                <p style={{ fontSize: isMobile ? 14 : 14, fontWeight: 600, color: colors.gray800, margin: 0 }}>
                  {filteredPrices.length} {pharmacyFilter || ''} pharmacies found
                  {pharmacyFilter && prices.length !== filteredPrices.length && (
                    <span style={{ fontWeight: 400, color: colors.gray500, fontSize: 12 }}>
                      {' '}(of {prices.length} total)
                    </span>
                  )}
                </p>
              </div>
              <div style={{ flex: 1, overflow: 'auto' }}>
                {filteredPrices.map((price, i) => (
                  <div
                    key={i}
                    onClick={() => focusPharmacy(i)}
                    style={{
                      padding: isMobile ? 14 : 14, borderBottom: `1px solid ${colors.gray100}`,
                      cursor: 'pointer', background: 'white'
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <div style={{ display: 'flex', gap: isMobile ? 10 : 10, flex: 1 }}>
                        <PharmacyLogoImage name={price.pharmacyName} logoUrl={price.pharmacyLogo} size={isMobile ? 36 : 40} />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2, flexWrap: 'wrap' }}>
                            <span style={{ fontWeight: 600, color: colors.gray900, fontSize: isMobile ? 14 : 14 }}>
                              {formatName(price.pharmacyName)}
                            </span>
                            {i === 0 && (
                              <span style={{
                                fontSize: 9, fontWeight: 700, background: colors.success,
                                color: 'white', padding: '2px 6px', borderRadius: 4
                              }}>LOWEST</span>
                            )}
                          </div>
                          {price.distance != null && (
                            <p style={{ fontSize: isMobile ? 12 : 12, color: colors.gray500, margin: 0, display: 'flex', alignItems: 'center', gap: 4 }}>
                              <MapPin size={12} /> {price.distance.toFixed(1)} mi
                            </p>
                          )}
                        </div>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <p style={{ fontSize: isMobile ? 18 : 20, fontWeight: 700, color: colors.success, margin: 0 }}>
                          ${price.price.toFixed(2)}
                        </p>
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 8, marginTop: isMobile ? 10 : 10 }}>
                      <button
                        onClick={(e) => { e.stopPropagation(); handleGetCoupon(price); }}
                        style={{
                          flex: 1, padding: isMobile ? '8px' : '8px',
                          background: colors.success, color: 'white', border: 'none',
                          borderRadius: 6, fontSize: isMobile ? 12 : 12, fontWeight: 600, cursor: 'pointer',
                          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4
                        }}
                      >
                        <Ticket size={isMobile ? 12 : 12} /> Coupon
                      </button>
                      <button
                        onClick={(e) => { 
                          e.stopPropagation(); 
                          window.open(`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(price.address || price.pharmacyName + ' near ' + zipCode)}`, '_blank');
                        }}
                        style={{
                          flex: 1, padding: isMobile ? '8px' : '8px',
                          background: colors.primary, color: 'white', border: 'none',
                          borderRadius: 6, fontSize: isMobile ? 12 : 12, fontWeight: 600, cursor: 'pointer',
                          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4
                        }}
                      >
                        <MapPin size={isMobile ? 12 : 12} /> Directions
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ) : (
          /* LIST VIEW */
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {filteredPrices.map((price, i) => (
              <div
                key={i}
                style={{
                  padding: isMobile ? 16 : 20, 
                  background: 'white', 
                  borderRadius: 12,
                  border: i === 0 ? `2px solid ${colors.success}` : `1px solid ${colors.gray200}`
                }}
              >
                {/* Pharmacy Info Row */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 14 }}>
                  <PharmacyLogoImage name={price.pharmacyName} logoUrl={price.pharmacyLogo} size={isMobile ? 48 : 56} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 4 }}>
                      <span style={{ fontSize: isMobile ? 16 : 18, fontWeight: 600, color: colors.gray900 }}>
                        {formatName(price.pharmacyName)}
                      </span>
                      {i === 0 && (
                        <span style={{
                          padding: '3px 8px', background: colors.success,
                          color: 'white', borderRadius: 4, fontSize: 10, fontWeight: 700
                        }}>
                          LOWEST
                        </span>
                      )}
                    </div>
                    {price.distance != null && (
                      <div style={{ fontSize: 13, color: colors.gray500, display: 'flex', alignItems: 'center', gap: 4 }}>
                        <MapPin size={14} /> {price.distance.toFixed(1)} mi away
                      </div>
                    )}
                  </div>
                  
                  {/* Price */}
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: isMobile ? 26 : 32, fontWeight: 700, color: colors.success }}>
                      ${price.price.toFixed(2)}
                    </div>
                    {price.retailPrice && (
                      <div style={{ fontSize: 12, color: colors.gray400, textDecoration: 'line-through' }}>
                        ${price.retailPrice.toFixed(2)}
                      </div>
                    )}
                  </div>
                </div>

                {/* Action Buttons */}
                <div style={{ display: 'flex', gap: 10 }}>
                  <button
                    onClick={() => handleGetCoupon(price)}
                    style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                      padding: '14px 16px', 
                      background: colors.success,
                      color: 'white', border: 'none', borderRadius: 8,
                      fontSize: 15, fontWeight: 600, cursor: 'pointer',
                      flex: 1
                    }}
                  >
                    <Ticket size={18} /> Get Coupon
                  </button>
                  <button
                    onClick={() => {
                      window.open(`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(price.address || price.pharmacyName + ' near ' + zipCode)}`, '_blank');
                    }}
                    style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                      padding: '14px 16px', 
                      background: colors.primary,
                      color: 'white', border: 'none', borderRadius: 8,
                      fontSize: 15, fontWeight: 600, cursor: 'pointer',
                      flex: 1
                    }}
                  >
                    <MapPin size={18} /> Directions
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Card CTA */}
        {prices.length > 0 && (
          <div style={{
            marginTop: isMobile ? 28 : 48, 
            padding: isMobile ? 28 : 48, 
            textAlign: 'center',
            background: 'white',
            borderRadius: 16, 
            border: `1px solid ${colors.gray200}`
          }}>
            <h3 style={{ fontSize: isMobile ? 22 : 28, fontWeight: 700, marginBottom: 12, color: colors.gray900 }}>
              Get Your Free Discount Card
            </h3>
            <p style={{ fontSize: isMobile ? 14 : 16, color: colors.gray600, marginBottom: 24, maxWidth: 500, margin: '0 auto 24px' }}>
              Save your card to your phone and use it every time
            </p>
            <button
              onClick={() => navigate('/card')}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 10,
                padding: isMobile ? '14px 28px' : '16px 36px', 
                background: colors.primary,
                color: 'white', border: 'none', borderRadius: 10,
                fontSize: isMobile ? 15 : 17, fontWeight: 600, cursor: 'pointer'
              }}
            >
              <CreditCard size={20} /> Get My Free Card
            </button>
          </div>
        )}

        {/* Telehealth Banner */}
        {prices.length > 0 && (
          <TelehealthBanner drugName={formatName(drugName)} />
        )}
      </div>

      {/* Coupon Modal */}
      <CouponModal
        isOpen={showCouponModal}
        onClose={() => { setShowCouponModal(false); setSelectedCoupon(null); }}
        drugName={selectedCoupon?.drugName || selectedDrugGroup?.drugName || drugName}
        pharmacy={selectedCoupon?.pharmacy}
        price={selectedCoupon?.price}
        quantity={selectedCoupon?.quantity || quantity}
        ndc={selectedCoupon?.ndc || selectedDrug?.ndc}
        zip={selectedCoupon?.zip || zipCode}
        dosage={selectedCoupon?.dosage || formatDosage(selectedDrug)}
      />

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

export default DrugPrices;